import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Columns2, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const FIELDS = [
  ["status", "Status"], ["question_kjv", "Question (KJV)"], ["question_esv", "Question (ESV)"], ["who_asked", "Asked by"], ["who_asked_detail", "Asked by details"],
  ["asked_to", "Asked to"], ["asked_to_detail", "Asked to details"], ["question_types", "Types"], ["themes", "Themes"], ["pericope", "Pericope"],
  ["purpose", "Why it was asked"], ["response", "The response"], ["historical_context", "Historical context"], ["commentary", "Bigger picture"], ["application", "For your life today"],
];
const show = (v) => (Array.isArray(v) ? v.join(", ") : v || "");

function Cell({ value, other, testId }) {
  const same = show(value) === show(other);
  return <td className={`align-top px-3 py-2 text-sm leading-relaxed ${same ? "text-muted-foreground" : "text-foreground"}`} data-testid={testId}>{show(value) || <span className="italic text-muted-foreground/60">—</span>}</td>;
}

function CompareDialog({ open, onClose, current, other, onDeleteCurrent, onOpenOther }) {
  if (!other) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-6xl overflow-y-auto" data-testid="duplicate-compare-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Side-by-side: {current.reference} vs {other.reference}</DialogTitle>
          <p className="text-sm text-muted-foreground">{other.match === "same verse" ? "Both questions are on the same verse." : `Wording is ${Math.round(other.similarity * 100)}% similar.`} Differences are shown in dark text; matching fields are greyed.</p>
        </DialogHeader>
        <table className="w-full table-fixed border-collapse">
          <thead>
            <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="w-36 px-3 py-2">Field</th>
              <th className="px-3 py-2">This question <span className="ml-1 rounded-full bg-gold/15 px-2 py-0.5 text-gold">{current.status}</span></th>
              <th className="px-3 py-2">{other.reference} <span className={`ml-1 rounded-full px-2 py-0.5 ${other.status === "published" ? "bg-emerald-500/15 text-emerald-700" : "bg-gold/15 text-gold"}`}>{other.status}</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {FIELDS.map(([k, label]) => (
              <tr key={k}>
                <th scope="row" className="px-3 py-2 text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</th>
                <Cell value={current[k]} other={other[k]} testId={`compare-current-${k}`} />
                <Cell value={other[k]} other={current[k]} testId={`compare-other-${k}`} />
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button variant="outline" className="rounded-full" onClick={onClose} data-testid="compare-keep-both">Keep both</Button>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenOther(other.id)} data-testid="compare-open-other">Review {other.reference} instead</Button>
          <Button variant="ghost" className="rounded-full text-destructive hover:text-destructive" onClick={onDeleteCurrent} data-testid="compare-delete-current"><Trash2 className="mr-2 h-4 w-4" /> Delete this {current.status === "published" ? "question" : "draft"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* Warns when the question under review shares a verse or wording with another question, and offers a side-by-side comparison. */
export function DuplicateCheck({ question, onOpenOther }) {
  const qc = useQueryClient();
  const [compare, setCompare] = useState(null);
  const { data } = useQuery({ queryKey: ["admin-duplicates", question.id], queryFn: async () => (await api.get(`/admin/questions/${question.id}/duplicates`)).data, enabled: !!question.id });
  const dups = data?.duplicates || [];
  if (!dups.length) return null;

  const deleteCurrent = async () => {
    if (!window.confirm(`Delete ${question.reference} (${question.status})? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/questions/${question.id}`);
      toast.success(`Deleted ${question.reference}.`);
      ["admin-questions", "admin-overview", "questions", "admin-duplicates"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      setCompare(null);
      onOpenOther(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="rounded-2xl border border-amber-400/50 bg-amber-50/70 p-4 dark:bg-amber-950/20" data-testid="duplicate-warning">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-foreground">Possible duplicate{dups.length > 1 ? "s" : ""}</p>
          <ul className="mt-2 space-y-2">
            {dups.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" data-testid={`duplicate-item-${d.id}`}>
                <span className="font-mono text-xs text-gold">{d.reference}</span>
                <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${d.status === "published" ? "bg-emerald-500/15 text-emerald-700" : "bg-gold/15 text-gold"}`}>{d.status}</span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{d.match}{d.match !== "same verse" && ` · ${Math.round(d.similarity * 100)}%`}</span>
                <span className="min-w-0 flex-1 truncate font-serif text-foreground">{d.question_kjv}</span>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setCompare(d)} data-testid={`duplicate-compare-${d.id}`}><Columns2 className="mr-1.5 h-3.5 w-3.5" /> Compare side by side</Button>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <CompareDialog open={!!compare} onClose={() => setCompare(null)} current={question} other={compare} onDeleteCurrent={deleteCurrent} onOpenOther={(id) => { setCompare(null); onOpenOther(id); }} />
    </div>
  );
}
